/**
 * Shared client for the Payroll Engine — the ONLY place the frontend
 * computes or reads payroll totals. Dashboard, Executive Brief, Needs Your
 * Attention, MAVIS Chat, and Reports MUST call these helpers.
 */
import { supabase } from "@/integrations/supabase/client";

type Action =
  | "list_semesters"
  | "upcoming_summary"
  | "generate_period"
  | "confirm_payment"
  | "record_adjustment"
  | "history"
  | "explain"
  | "exceptions_alerts"
  | "list_periods"
  | "reconcile_historical"
  | "my_permissions"
  | "class_payroll_summary";

export class PayrollEngineError extends Error {
  code: string;
  details?: any;
  constructor(message: string, code = "payroll_unknown_error", details?: any) {
    super(message);
    this.name = "PayrollEngineError";
    this.code = code;
    this.details = details;
  }
}

/**
 * supabase.functions.invoke() throws a FunctionsHttpError whose message is the
 * useless "Edge Function returned a non-2xx status code" — the real payload is
 * on error.context (a Response). Always read it so operators see a human error.
 */
async function readEngineError(error: any): Promise<PayrollEngineError> {
  const res: Response | undefined = error?.context instanceof Response ? error.context : undefined;
  if (res) {
    try {
      const body = await res.clone().json();
      if (body?.error) {
        return new PayrollEngineError(String(body.error), body.code ?? `http_${res.status}`, body);
      }
    } catch {
      try {
        const text = await res.clone().text();
        if (text) return new PayrollEngineError(text, `http_${res.status}`);
      } catch { /* ignore */ }
    }
    return new PayrollEngineError(
      "We couldn't reach the payroll service. Nothing was posted to the ledger. Please try again.",
      `http_${res.status}`,
    );
  }
  return new PayrollEngineError(
    error?.message && !/non-2xx/i.test(error.message)
      ? error.message
      : "We couldn't reach the payroll service. Nothing was posted to the ledger. Please try again.",
    "payroll_network_error",
  );
}

export async function callPayrollEngine<T = any>(action: Action, body: Record<string, unknown> = {}): Promise<T> {
  const { data, error } = await supabase.functions.invoke("payroll-engine", { body: { action, ...body } });
  if (error) throw await readEngineError(error);
  if (data && typeof data === "object" && (data as any).error) {
    throw new PayrollEngineError(String((data as any).error), (data as any).code ?? "payroll_error", data);
  }
  return data as T;
}

export interface PayrollPaymentDetails {
  method: string | null;
  zelle_name: string | null;
  zelle_contact: string | null;
  zelle_recipient_type: "email" | "phone" | null;
  notes: string | null;
  verified_at: string | null;
}

export interface PayrollLineItem {
  line_item_id?: string;
  period_id?: string;
  restored_from_persisted?: boolean;
  instructor_id: string;
  instructor_name: string;
  instructor_full_name?: string | null;
  instructor_preferred_name?: string | null;
  computed_amount_cents: number;
  pay_rate_snapshot: any;
  payment_method_snapshot: string | null;
  payment_details?: PayrollPaymentDetails;
  payment_details_missing?: boolean;
  sessions_count: number;
  classes_snapshot: Array<{
    class_title: string; class_slug: string; session_label: string | null;
    sessions_in_period: number; subtotal_cents?: number;
    pay_rate_type?: string; pay_rate_amount?: number;
    meeting_dates?: string[];
    previously_paid_dates?: string[];
  }>;
  status: "ready" | "waiting_info" | "confirmed_paid" | "exception" | "skipped";
  missing_info: string[];
  is_exception: boolean;
  exception_reason: string | null;
}

export interface ReconciliationRow {
  instructor_id: string;
  instructor_name: string;
  rate: { pay_rate_type: string | null; pay_rate_amount: number | null };
  eligible: Array<{ class_title: string; session_label: string | null; meeting_dates: string[]; subtotal_cents: number }>;
  correct_gross_cents: number;
  stored_ledger_id: string | null;
  stored_cents: number;
  difference_cents: number;
  reason?: string;
}

export type PayrollDueState = "upcoming" | "due_today" | "overdue";

export interface UpcomingSummary {
  period: {
    id?: string | null;
    due_state?: PayrollDueState;
    type: string; semester: "spring" | "summer" | "fall"; semester_month: number | null;
    pay_date: string; period_start: string; period_end: string;
  };
  period_id?: string | null;
  due_state?: PayrollDueState;
  is_overdue?: boolean;
  days_remaining: number;
  instructor_count: number;
  total_estimated_cents: number;
  cash_cents: number;
  electronic_cents: number;
  outstanding_confirmations: number;
  exceptions: PayrollLineItem[];
  line_items: PayrollLineItem[];
}

export interface SemesterInfo {
  key: "spring" | "summer" | "fall";
  label: string;
  months: number[];
  periods: Array<{
    type: string; semester: string; semester_month: number;
    pay_date: string; period_start: string; period_end: string;
  }>;
}

export interface LedgerEntry {
  id: string;
  instructor_id: string;
  period_id: string | null;
  semester: string;
  semester_month: number | null;
  classes_snapshot: any[];
  rate_snapshot: any;
  gross_cents: number;
  adjustments_cents: number;
  final_cents: number;
  payment_method: string;
  paid_at: string;
  confirmed_by: string | null;
  confirmed_by_label: string | null;
  notes: string | null;
  receipt_url: string | null;
  entry_kind: "payment" | "adjustment" | "historical";
  corrects_ledger_id: string | null;
  immutable_hash: string;
  created_at: string;
  instructors?: { full_name: string; preferred_name: string | null };
  payroll_periods?: { pay_date: string; period_start: string; period_end: string; type: string; semester: string; semester_month: number | null };
}

export const getUpcomingPayroll = () => callPayrollEngine<UpcomingSummary>("upcoming_summary");
export const listSemesters = () => callPayrollEngine<{ year: number; current_semester: string; semesters: SemesterInfo[] }>("list_semesters");
export const generatePayrollPeriod = (pay_date?: string) => callPayrollEngine("generate_period", { pay_date });
export const confirmPayrollPayment = (payload: {
  line_item_id: string; amount_cents?: number; payment_method?: string;
  notes?: string; receipt_url?: string; paid_at?: string;
  reference_number?: string;
  payment_destination_snapshot?: Record<string, unknown>;
  processed_by_label?: string;
}) => callPayrollEngine<{ confirmation: any; ledger_entry: any; already_paid?: boolean }>("confirm_payment", payload);

/** The exact payment methods the database accepts. Never send anything else. */
export const PAYROLL_PAYMENT_METHODS = [
  { value: "zelle", label: "Zelle" },
  { value: "cash", label: "Cash" },
  { value: "check", label: "Check" },
  { value: "ach", label: "ACH / bank transfer" },
  { value: "paypal", label: "PayPal" },
  { value: "venmo", label: "Venmo" },
  { value: "other", label: "Other" },
] as const;

export function normalizePayrollMethod(raw?: string | null): string {
  const v = (raw ?? "").trim().toLowerCase();
  if (!v || v === "unknown" || v === "select method…" || v === "select method...") return "";
  const hit = PAYROLL_PAYMENT_METHODS.find((m) => m.value === v);
  if (hit) return hit.value;
  if (v.includes("zelle")) return "zelle";
  if (v.includes("check")) return "check";
  if (v.includes("cash")) return "cash";
  if (v.includes("ach") || v.includes("bank")) return "ach";
  if (v.includes("paypal")) return "paypal";
  if (v.includes("venmo")) return "venmo";
  return "other";
}
export const getMyPayrollPermissions = () =>
  callPayrollEngine<{ user_id: string | null; payroll_view: boolean; payroll_process: boolean }>("my_permissions");
export const recordPayrollAdjustment = (payload: {
  instructor_id: string; corrects_ledger_id?: string; delta_cents: number;
  payment_method?: string; notes?: string; paid_at?: string;
}) => callPayrollEngine("record_adjustment", payload);
export const getPayrollHistory = (filters: {
  instructor_id?: string; semester?: string; semester_month?: number; from?: string; to?: string;
} = {}) => callPayrollEngine<{ rows: LedgerEntry[] }>("history", filters);
export const explainLedgerEntry = (ledger_id: string) =>
  callPayrollEngine<{ entry: LedgerEntry }>("explain", { ledger_id });
export const getPayrollExceptionsAlerts = () => callPayrollEngine<{ rules: any[] }>("exceptions_alerts");
export const listPayrollPeriods = () => callPayrollEngine<{ periods: any[] }>("list_periods");
export const reconcileHistoricalPayroll = () =>
  callPayrollEngine<{ period_start: string; period_end: string; rows: ReconciliationRow[] }>("reconcile_historical");

export interface ClassPayrollInstructor {
  instructor_id: string;
  instructor_name: string;
  status: string | null;
  meeting_count: number;
  projected_cents: number;
  paid_cents: number;
  remaining_cents: number;
  session_labels: string[];
}

export interface ClassPayrollSummary {
  class_slug: string | null;
  session_label: string | null;
  instructors: ClassPayrollInstructor[];
  totals: { projected_cents: number; paid_cents: number; remaining_cents: number };
  basis?: string;
}

/** Class-scoped payroll straight from the payroll engine. Never recompute. */
export const getClassPayrollSummary = (class_slug: string, session_label?: string | null) =>
  callPayrollEngine<ClassPayrollSummary>("class_payroll_summary", { class_slug, session_label: session_label ?? null });

export const money = (cents: number) => `$${(cents / 100).toFixed(2)}`;
